/**
 * App — router only. No business logic per MONOREPO.md §4.1.
 *
 * Routes (Phase 2a + 2b + 3a + 3b — staff auth + dashboard + mail + ops + KYC/gov/SD/printing):
 *   /auth/login              → StaffLoginPage (full-bleed, no chrome)
 *   /login                   → redirect to /auth/login (back-compat with Phase 0)
 *
 *   / (RequireAuth + StaffLayout chrome):
 *     /                            → StaffDashboardPage
 *     /approvals                   → ApprovalQueuePage
 *     /mail/...                    → mail surface (Phase 2b)
 *     /delivery, /fleet, /customer-service/...   → ops slice 1 (Phase 3a)
 *
 *     /kyc                          → KYCDashboardPage              (3b)
 *     /kyc/verify                   → KYCVerifyPage                 (3b)
 *     /kyc/analytics                → KYCAnalyticsPage              (3b)
 *     /kyc/compliance               → KYCCompliancePage             (3b)
 *     /kyc/sar                      → SARWorkflowPage               (3b)
 *     /kyc/watchlist[/:id]          → WatchlistDetailPage           (3b)
 *
 *     /government                   → GovernmentDashboardPage       (3b)
 *     /government/agencies          → GovernmentAgenciesPage        (3b)
 *     /government/intake            → GovernmentIntakePage          (3b)
 *     /government/reports           → GovServiceReportsPage         (3b)
 *     /government/remittance        → MDARemittancePage             (3b, deferred)
 *     /government/citizen-lookup    → CitizenStatusLookupPage       (3b)
 *
 *     /service-desk                 → ServiceDeskDashboardPage      (3b, deferred)
 *     /service-desk/applications    → ApplicationsPage              (3b, deferred)
 *     /service-desk/forms           → FormBuilderPage               (3b, local-only)
 *     /service-desk/notifications   → NotificationEditorPage        (3b, local-only)
 *     /service-desk/data-export     → DataExportPage                (3b, deferred)
 *     /service-desk/help-center     → HelpCenterPage                (3b)
 *
 *     /printing                     → PrintingDashboardPage         (3b)
 *     /printing/printers            → PrinterManagementPage         (3b)
 *     /printing/templates           → PrintTemplatesPage            (3b)
 *     /printing/templates/:id       → TemplateEditorPage            (3b)
 *
 *     /franchise                                  → FranchiseDashboardPage    (4a)
 *     /franchise/applications                     → FranchiseApplicationsPage (4a)
 *     /franchise/applications/new                 → FranchiseApplyPage        (4a)
 *     /franchise/applications/:id/onboard         → FranchiseOnboardingPage   (4a)
 *     /franchise/franchises/:id                   → FranchiseDetailPage       (4a)
 *     /franchise/hierarchy                        → FranchiseHierarchyPage    (4a)
 *     /franchise/agreements                       → AgreementManagementPage   (4a)
 *     /franchise/tiers                            → TierDefinitionsPage       (4a)
 *     /franchise/tiers/:franchiseId/upgrade       → TierUpgradeDowngradePage  (4a)
 *     /franchise/services                         → ServiceCataloguePage      (4a)
 *     /franchise/provisioning                     → ProvisioningWizardPage    (4a)
 *     /franchise/devices                          → DeviceProvisioningPage    (4a)
 *     /franchise/certification                    → CertificationTrackerPage  (4a)
 *
 *     /franchise/national-map                     → NationalMapPage                  (4b)
 *     /franchise/settlements                      → FranchiseSettlementTrackingPage  (4b)
 *     /franchise/remittance                       → RemittanceSchedulingPage         (4b)
 *     /franchise/revenue-share                    → RevenueShareDisplayPage          (4b)
 *     /franchise/incentives                       → IncentiveCalculationPage         (4b)
 *     /franchise/performance                      → FranchisePerformancePage         (4b)
 *     /franchise/reconciliation                   → FranchiseReconciliationPage      (4b)
 *     /franchise/suspension                       → FranchiseSuspensionPage          (4b)
 *     /franchise/inventory                        → InventoryManagementPage          (4b)
 *     /franchise/training                         → TrainingDashboardPage            (4b)
 *     /franchise/transactions                     → TransactionLogViewerPage         (4b)
 *     /franchise/discrepancies                    → DiscrepancyDetectionPage         (4b)
 *     /franchise/broadcasts                       → BroadcastsPage                   (4b)
 *
 *     /security                    → SecurityDashboardPage    (4c)
 *     /security/users              → UserManagementPage       (4c, deferred)
 *     /security/users/:id          → UserProfilePage          (4c, deferred)
 *     /security/roles              → RoleManagementPage       (4c, static)
 *     /security/field-permissions  → FieldPermissionsPage     (4c, deferred)
 *     /security/audit-log          → AuditLogPage             (4c, partial)
 *
 *     /financial-services                → FinancialServicesDashboardPage  (4c)
 *     /financial-services/bill-payments  → BillPaymentsPage                (4c)
 *     /financial-services/transfers      → MoneyTransfersPage              (4c)
 *     /financial-services/pos            → POSTransactionsPage             (4c)
 *     /financial-services/fraud          → FraudAlertsPage                 (4c)
 *
 *     *                            → fallback redirect to /
 *
 *
 *   Driver portal (Phase 7c — opts out of StaffLayout, renders DriverPortalLayout):
 *     /driver-portal                  → DriverOverviewPage
 *     /driver-portal/schedule         → DriverSchedulePage
 *     /driver-portal/deliveries       → DriverDeliveriesPage
 *     /driver-portal/scorecard        → DriverScorecardPage
 *     /driver-portal/earnings         → DriverEarningsPage
 *     /driver-portal/notifications    → DriverNotificationsPage
 *     /driver-portal/safety           → DriverSafetyPage
 *     /driver-portal/training         → DriverTrainingPage
 *     /driver-portal/profile          → DriverProfilePage
 *     /driver-portal/help             → DriverHelpPage
 *
 * Phase 5+ wires reporting / settings — still disabled-with-tooltip in StaffLayout.
 */
import type { JSX } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { ApprovalQueuePage } from './features/approvals/ApprovalQueuePage';
import { StaffLoginPage } from './features/auth/StaffLoginPage';
import { ComplaintCreatePage } from './features/customer-service/ComplaintCreatePage';
import { ComplaintDetailPage } from './features/customer-service/ComplaintDetailPage';
import { ComplaintsListPage } from './features/customer-service/ComplaintsListPage';
import { CustomerLookupPage } from './features/customer-service/CustomerLookupPage';
import { CustomerSegmentationPage } from './features/customer-service/CustomerSegmentationPage';
import { CustomerServiceDashboardPage } from './features/customer-service/CustomerServiceDashboardPage';
import { EscalationManagementPage } from './features/customer-service/EscalationManagementPage';
import { FeedbackPage } from './features/customer-service/FeedbackPage';
import { StaffPublicTrackingPage } from './features/customer-service/StaffPublicTrackingPage';
import { StaffDashboardPage } from './features/dashboard/StaffDashboardPage';
import { DeliveryAssignmentPage } from './features/delivery/DeliveryAssignmentPage';
import { DeliveryDashboardPage } from './features/delivery/DeliveryDashboardPage';
import { DeliveryDetailPage } from './features/delivery/DeliveryDetailPage';
import { DeliveryDisputePage } from './features/delivery/DeliveryDisputePage';
import { DeliveryExceptionsPage } from './features/delivery/DeliveryExceptionsPage';
import { DeliveryPerformancePage } from './features/delivery/DeliveryPerformancePage';
import { DeliveryPODReviewPage } from './features/delivery/DeliveryPODReviewPage';
import { DeliveryRoutesPage } from './features/delivery/DeliveryRoutesPage';
import { DriverManagementPage } from './features/delivery/DriverManagementPage';
import { RouteBuilderPage } from './features/delivery/RouteBuilderPage';
import { BillPaymentsPage } from './features/financial-services/BillPaymentsPage';
import { FinancialServicesDashboardPage } from './features/financial-services/FinancialServicesDashboardPage';
import { FraudAlertsPage } from './features/financial-services/FraudAlertsPage';
import { MoneyTransfersPage } from './features/financial-services/MoneyTransfersPage';
import { POSTransactionsPage } from './features/financial-services/POSTransactionsPage';
import { FleetDashboardPage } from './features/fleet/FleetDashboardPage';
import { AgreementManagementPage } from './features/franchise/AgreementManagementPage';
import { BroadcastsPage } from './features/franchise/BroadcastsPage';
import { CertificationTrackerPage } from './features/franchise/CertificationTrackerPage';
import { DeviceProvisioningPage } from './features/franchise/DeviceProvisioningPage';
import { DiscrepancyDetectionPage } from './features/franchise/DiscrepancyDetectionPage';
import { FranchiseApplicationsPage } from './features/franchise/FranchiseApplicationsPage';
import { FranchiseApplyPage } from './features/franchise/FranchiseApplyPage';
import { FranchiseDashboardPage } from './features/franchise/FranchiseDashboardPage';
import { FranchiseDetailPage } from './features/franchise/FranchiseDetailPage';
import { FranchiseHierarchyPage } from './features/franchise/FranchiseHierarchyPage';
import { FranchiseOnboardingPage } from './features/franchise/FranchiseOnboardingPage';
import { FranchisePerformancePage } from './features/franchise/FranchisePerformancePage';
import { FranchiseReconciliationPage } from './features/franchise/FranchiseReconciliationPage';
import { FranchiseSettlementTrackingPage } from './features/franchise/FranchiseSettlementTrackingPage';
import { FranchiseSuspensionPage } from './features/franchise/FranchiseSuspensionPage';
import { IncentiveCalculationPage } from './features/franchise/IncentiveCalculationPage';
import { InventoryManagementPage } from './features/franchise/InventoryManagementPage';
import { NationalMapPage } from './features/franchise/NationalMapPage';
import { ProvisioningWizardPage } from './features/franchise/ProvisioningWizardPage';
import { RemittanceSchedulingPage } from './features/franchise/RemittanceSchedulingPage';
import { RevenueShareDisplayPage } from './features/franchise/RevenueShareDisplayPage';
import { ServiceCataloguePage } from './features/franchise/ServiceCataloguePage';
import { TierDefinitionsPage } from './features/franchise/TierDefinitionsPage';
import { TierUpgradeDowngradePage } from './features/franchise/TierUpgradeDowngradePage';
import { TrainingDashboardPage } from './features/franchise/TrainingDashboardPage';
import { TransactionLogViewerPage } from './features/franchise/TransactionLogViewerPage';
import { VehicleDetailPage } from './features/fleet/VehicleDetailPage';
import { VehicleRegisterPage } from './features/fleet/VehicleRegisterPage';
import { DriverDeliveriesPage } from './features/driver-portal/DriverDeliveriesPage';
import { DriverEarningsPage } from './features/driver-portal/DriverEarningsPage';
import { DriverHelpPage } from './features/driver-portal/DriverHelpPage';
import { DriverNotificationsPage } from './features/driver-portal/DriverNotificationsPage';
import { DriverOverviewPage } from './features/driver-portal/DriverOverviewPage';
import { DriverPortalLayout } from './features/driver-portal/DriverPortalLayout';
import { DriverProfilePage } from './features/driver-portal/DriverProfilePage';
import { DriverSafetyPage } from './features/driver-portal/DriverSafetyPage';
import { DriverSchedulePage } from './features/driver-portal/DriverSchedulePage';
import { DriverScorecardPage } from './features/driver-portal/DriverScorecardPage';
import { DriverTrainingPage } from './features/driver-portal/DriverTrainingPage';
import { CitizenStatusLookupPage } from './features/government/CitizenStatusLookupPage';
import { GovernmentAgenciesPage } from './features/government/GovernmentAgenciesPage';
import { GovernmentDashboardPage } from './features/government/GovernmentDashboardPage';
import { GovernmentIntakePage } from './features/government/GovernmentIntakePage';
import { GovServiceReportsPage } from './features/government/GovServiceReportsPage';
import { MDARemittancePage } from './features/government/MDARemittancePage';
import { KYCAnalyticsPage } from './features/kyc/KYCAnalyticsPage';
import { KYCCompliancePage } from './features/kyc/KYCCompliancePage';
import { KYCDashboardPage } from './features/kyc/KYCDashboardPage';
import { KYCVerifyPage } from './features/kyc/KYCVerifyPage';
import { SARWorkflowPage } from './features/kyc/SARWorkflowPage';
import { WatchlistDetailPage } from './features/kyc/WatchlistDetailPage';
import { MailDraftsListPage } from './features/mail-register/MailDraftsListPage';
import { MailRegisterPage } from './features/mail-register/MailRegisterPage';
import { BulkMailRegistrationPage } from './features/mail/BulkMailRegistrationPage';
import { CashReconciliationPage } from './features/mail/CashReconciliationPage';
import { CounterPaymentPage } from './features/mail/CounterPaymentPage';
import { CrossDockManifestsPage } from './features/mail/CrossDockManifestsPage';
import { CustomerProfileManagementPage } from './features/mail/CustomerProfileManagementPage';
import { DailyTransactionSummaryPage } from './features/mail/DailyTransactionSummaryPage';
import { DamagedItemHandlingPage } from './features/mail/DamagedItemHandlingPage';
import { ErrorResolutionPage } from './features/mail/ErrorResolutionPage';
import { LabelGenerationPage } from './features/mail/LabelGenerationPage';
import { MailDashboardPage } from './features/mail/MailDashboardPage';
import { MailItemDetailPage } from './features/mail/MailItemDetailPage';
import { MailItemsListPage } from './features/mail/MailItemsListPage';
import { MailTrackingPage } from './features/mail/MailTrackingPage';
import { NINVerifyPage } from './features/mail/NINVerifyPage';
import { POBoxManagementPage } from './features/mail/POBoxManagementPage';
import { ReceiptPreviewPage } from './features/mail/ReceiptPreviewPage';
import { ReturnToSenderPage } from './features/mail/ReturnToSenderPage';
import { ShiftManagementPage } from './features/mail/ShiftManagementPage';
import { SortingWorkflowPage } from './features/mail/SortingWorkflowPage';
import { SupervisorApprovalsPage } from './features/mail/SupervisorApprovalsPage';
import { WeightDiscrepancyPage } from './features/mail/WeightDiscrepancyPage';
import { PrinterManagementPage } from './features/printing/PrinterManagementPage';
import { PrintingDashboardPage } from './features/printing/PrintingDashboardPage';
import { PrintTemplatesPage } from './features/printing/PrintTemplatesPage';
import { TemplateEditorPage } from './features/printing/TemplateEditorPage';
import { AuditLogPage } from './features/security/AuditLogPage';
import { FieldPermissionsPage } from './features/security/FieldPermissionsPage';
import { RoleManagementPage } from './features/security/RoleManagementPage';
import { SecurityDashboardPage } from './features/security/SecurityDashboardPage';
import { UserManagementPage } from './features/security/UserManagementPage';
import { UserProfilePage } from './features/security/UserProfilePage';
import { AdHocQueryPage } from './features/reporting/AdHocQueryPage';
import { AlertConfigurationPage } from './features/reporting/AlertConfigurationPage';
import { BenchmarkComparisonsPage } from './features/reporting/BenchmarkComparisonsPage';
import { DashboardBuilderPage } from './features/reporting/DashboardBuilderPage';
import { DashboardSharingPage } from './features/reporting/DashboardSharingPage';
import { DataMiningPage } from './features/reporting/DataMiningPage';
import { KPIConfigurationPage } from './features/reporting/KPIConfigurationPage';
import { ManagementKPIReportsPage } from './features/reporting/ManagementKPIReportsPage';
import { ReportAccessAuditPage } from './features/reporting/ReportAccessAuditPage';
import { ReportBuilderPage } from './features/reporting/ReportBuilderPage';
import { ReportingDashboardPage } from './features/reporting/ReportingDashboardPage';
import { ReportVersioningPage } from './features/reporting/ReportVersioningPage';
import { ReportViewerPage } from './features/reporting/ReportViewerPage';
import { RoleScopedDashboardsPage } from './features/reporting/RoleScopedDashboardsPage';
import { ScheduledReportsPage } from './features/reporting/ScheduledReportsPage';
import { TrendForecastingPage } from './features/reporting/TrendForecastingPage';
import { WhatIfScenariosPage } from './features/reporting/WhatIfScenariosPage';
import { AuditComplianceReportPage } from './features/reports/AuditComplianceReportPage';
import { DailyTransactionReportPage } from './features/reports/DailyTransactionReportPage';
import { DeliverySLAReportPage } from './features/reports/DeliverySLAReportPage';
import { FleetOperationsReportPage } from './features/reports/FleetOperationsReportPage';
import { FranchiseScorecardPage } from './features/reports/FranchiseScorecardPage';
import { GovServicesMonthlyPage } from './features/reports/GovServicesMonthlyPage';
import { KYCComplianceReportPage } from './features/reports/KYCComplianceReportPage';
import { ReconciliationReportPage } from './features/reports/ReconciliationReportPage';
import { ReportsIndexPage } from './features/reports/ReportsIndexPage';
import { RevenueSharingStatementPage } from './features/reports/RevenueSharingStatementPage';
import { StaffProductivityReportPage } from './features/reports/StaffProductivityReportPage';
import { CustomerDetailPage } from './features/admin/customers/CustomerDetailPage';
import { CustomersBrowsePage } from './features/admin/customers/CustomersBrowsePage';
import { CustomersDashboardPage } from './features/admin/customers/CustomersDashboardPage';
import { EmailTemplatesPage } from './features/admin/EmailTemplatesPage';
import { MVPAcceptancePage } from './features/admin/MVPAcceptancePage';
import { NotificationAdminPage } from './features/admin/NotificationAdminPage';
import { SitemapPage } from './features/admin/SitemapPage';
import { FlowchartDetailPage } from './features/flowcharts/FlowchartDetailPage';
import { FlowchartsIndexPage } from './features/flowcharts/FlowchartsIndexPage';
import { APIKeyManagementPage } from './features/integration/APIKeyManagementPage';
import { IntegrationDashboardPage } from './features/integration/IntegrationDashboardPage';
import { IntegrationHealthPage } from './features/integration/IntegrationHealthPage';
import { NIBSSIntegrationPage } from './features/integration/NIBSSIntegrationPage';
import { PartnerOnboardingPage } from './features/integration/PartnerOnboardingPage';
import { WebhookConfigPage } from './features/integration/WebhookConfigPage';
import { DiscountRulesPage } from './features/settings/DiscountRulesPage';
import { FraudRuleBuilderPage } from './features/settings/FraudRuleBuilderPage';
import { GovServiceConfigPage } from './features/settings/GovServiceConfigPage';
import { NotificationTemplatesPage } from './features/settings/NotificationTemplatesPage';
import { OutletManagementPage } from './features/settings/OutletManagementPage';
import { PricingConfigPage } from './features/settings/PricingConfigPage';
import { PricingSurchargesPage } from './features/settings/PricingSurchargesPage';
import { PricingZonesPage } from './features/settings/PricingZonesPage';
import { ServiceClassesPage } from './features/settings/ServiceClassesPage';
import { RecipientAccountsPage } from './features/settings/RecipientAccountsPage';
import { RevenueSharingConfigPage } from './features/settings/RevenueSharingConfigPage';
import { SettingsHubPage } from './features/settings/SettingsHubPage';
import { SLAConfigPage } from './features/settings/SLAConfigPage';
import { SystemParametersPage } from './features/settings/SystemParametersPage';
import { TierServiceMatrixPage } from './features/settings/TierServiceMatrixPage';
import { TransactionLimitsPage } from './features/settings/TransactionLimitsPage';
import { ApplicationsPage } from './features/service-desk/ApplicationsPage';
import { DataExportPage } from './features/service-desk/DataExportPage';
import { FormBuilderPage } from './features/service-desk/FormBuilderPage';
import { HelpCenterPage } from './features/service-desk/HelpCenterPage';
import { NotificationEditorPage } from './features/service-desk/NotificationEditorPage';
import { ServiceDeskDashboardPage } from './features/service-desk/ServiceDeskDashboardPage';
import { StaffLayout } from './layouts/StaffLayout';
import { useAuth } from './lib/auth/useAuth';

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }
  return children;
}

export function App(): JSX.Element {
  return (
    <Routes>
      {/* Full-bleed routes — no shared chrome. */}
      <Route path="/auth/login" element={<StaffLoginPage />} />
      <Route path="/login" element={<Navigate to="/auth/login" replace />} />

      {/* Driver portal — authenticated, but renders its own DriverPortalLayout
          chrome instead of the operator StaffLayout. Drivers share the staff
          JWT scheme (Phase 6 sign-in) but the surface is dedicated. */}
      <Route
        path="/driver-portal"
        element={
          <RequireAuth>
            <DriverPortalLayout />
          </RequireAuth>
        }
      >
        <Route index element={<DriverOverviewPage />} />
        <Route path="schedule" element={<DriverSchedulePage />} />
        <Route path="deliveries" element={<DriverDeliveriesPage />} />
        <Route path="scorecard" element={<DriverScorecardPage />} />
        <Route path="earnings" element={<DriverEarningsPage />} />
        <Route path="notifications" element={<DriverNotificationsPage />} />
        <Route path="safety" element={<DriverSafetyPage />} />
        <Route path="training" element={<DriverTrainingPage />} />
        <Route path="profile" element={<DriverProfilePage />} />
        <Route path="help" element={<DriverHelpPage />} />
      </Route>

      {/* Authenticated console — StaffLayout chrome. */}
      <Route
        element={
          <RequireAuth>
            <StaffLayout />
          </RequireAuth>
        }
      >
        <Route index element={<StaffDashboardPage />} />
        <Route path="approvals" element={<ApprovalQueuePage />} />

        {/* Mail (Phase 2b) */}
        <Route path="mail" element={<MailDashboardPage />} />
        <Route path="mail/register" element={<MailRegisterPage />} />
        <Route path="mail/register/bulk" element={<BulkMailRegistrationPage />} />
        <Route path="mail/register/:draftId" element={<MailRegisterPage />} />
        <Route path="mail/drafts" element={<MailDraftsListPage />} />
        <Route path="mail/items" element={<MailItemsListPage />} />
        <Route path="mail/items/:itemId" element={<MailItemDetailPage />} />
        <Route path="mail/track" element={<MailTrackingPage />} />
        <Route path="mail/labels" element={<LabelGenerationPage />} />
        <Route path="mail/receipts" element={<ReceiptPreviewPage />} />
        <Route path="mail/receipt/:trackingNumber" element={<ReceiptPreviewPage />} />
        <Route path="mail/sorting" element={<SortingWorkflowPage />} />
        <Route path="mail/manifests" element={<CrossDockManifestsPage />} />
        <Route path="mail/returns" element={<ReturnToSenderPage />} />
        <Route path="mail/damages" element={<DamagedItemHandlingPage />} />
        <Route path="mail/weight-discrepancy" element={<WeightDiscrepancyPage />} />
        <Route path="mail/errors" element={<ErrorResolutionPage />} />
        <Route path="mail/supervisor" element={<SupervisorApprovalsPage />} />
        <Route path="mail/cash-recon" element={<CashReconciliationPage />} />
        <Route path="mail/transactions/daily" element={<DailyTransactionSummaryPage />} />
        <Route path="mail/payments/counter" element={<CounterPaymentPage />} />
        <Route path="mail/shift" element={<ShiftManagementPage />} />
        <Route path="mail/pobox" element={<POBoxManagementPage />} />
        <Route path="mail/customer-profile" element={<CustomerProfileManagementPage />} />
        <Route path="mail/nin-verify" element={<NINVerifyPage />} />

        {/* Delivery (Phase 3a) */}
        <Route path="delivery" element={<DeliveryDashboardPage />} />
        <Route path="delivery/routes" element={<DeliveryRoutesPage />} />
        <Route path="delivery/routes/new" element={<RouteBuilderPage />} />
        <Route path="delivery/assignment" element={<DeliveryAssignmentPage />} />
        <Route path="delivery/exceptions" element={<DeliveryExceptionsPage />} />
        <Route path="delivery/disputes" element={<DeliveryDisputePage />} />
        <Route path="delivery/performance" element={<DeliveryPerformancePage />} />
        <Route path="delivery/drivers" element={<DriverManagementPage />} />
        <Route path="delivery/items/:id" element={<DeliveryDetailPage />} />
        <Route path="delivery/pod" element={<DeliveryPODReviewPage />} />
        <Route path="delivery/pod/:id" element={<DeliveryPODReviewPage />} />

        {/* Fleet (Phase 3a) */}
        <Route path="fleet" element={<FleetDashboardPage />} />
        <Route path="fleet/register" element={<VehicleRegisterPage />} />
        <Route path="fleet/vehicles/:id" element={<VehicleDetailPage />} />

        {/* Customer service (Phase 3a) */}
        <Route path="customer-service" element={<CustomerServiceDashboardPage />} />
        <Route path="customer-service/lookup" element={<CustomerLookupPage />} />
        <Route path="customer-service/complaints" element={<ComplaintsListPage />} />
        <Route path="customer-service/complaints/new" element={<ComplaintCreatePage />} />
        <Route path="customer-service/complaints/:id" element={<ComplaintDetailPage />} />
        <Route path="customer-service/segments" element={<CustomerSegmentationPage />} />
        <Route path="customer-service/escalations" element={<EscalationManagementPage />} />
        <Route path="customer-service/feedback" element={<FeedbackPage />} />
        <Route path="customer-service/track" element={<StaffPublicTrackingPage />} />

        {/* KYC (Phase 3b) */}
        <Route path="kyc" element={<KYCDashboardPage />} />
        <Route path="kyc/verify" element={<KYCVerifyPage />} />
        <Route path="kyc/analytics" element={<KYCAnalyticsPage />} />
        <Route path="kyc/compliance" element={<KYCCompliancePage />} />
        <Route path="kyc/sar" element={<SARWorkflowPage />} />
        <Route path="kyc/watchlist" element={<WatchlistDetailPage />} />
        <Route path="kyc/watchlist/:id" element={<WatchlistDetailPage />} />

        {/* Government (Phase 3b) */}
        <Route path="government" element={<GovernmentDashboardPage />} />
        <Route path="government/agencies" element={<GovernmentAgenciesPage />} />
        <Route path="government/intake" element={<GovernmentIntakePage />} />
        <Route path="government/reports" element={<GovServiceReportsPage />} />
        <Route path="government/remittance" element={<MDARemittancePage />} />
        <Route path="government/citizen-lookup" element={<CitizenStatusLookupPage />} />

        {/* Service Desk (Phase 3b) */}
        <Route path="service-desk" element={<ServiceDeskDashboardPage />} />
        <Route path="service-desk/applications" element={<ApplicationsPage />} />
        <Route path="service-desk/forms" element={<FormBuilderPage />} />
        <Route path="service-desk/notifications" element={<NotificationEditorPage />} />
        <Route path="service-desk/data-export" element={<DataExportPage />} />
        <Route path="service-desk/help-center" element={<HelpCenterPage />} />

        {/* Printing (Phase 3b) */}
        <Route path="printing" element={<PrintingDashboardPage />} />
        <Route path="printing/printers" element={<PrinterManagementPage />} />
        <Route path="printing/templates" element={<PrintTemplatesPage />} />
        <Route path="printing/templates/:id" element={<TemplateEditorPage />} />

        {/* Franchise — onboarding + ops side (Phase 4a) */}
        <Route path="franchise" element={<FranchiseDashboardPage />} />
        <Route path="franchise/applications" element={<FranchiseApplicationsPage />} />
        <Route path="franchise/applications/new" element={<FranchiseApplyPage />} />
        <Route path="franchise/applications/:id/onboard" element={<FranchiseOnboardingPage />} />
        <Route path="franchise/franchises/:id" element={<FranchiseDetailPage />} />
        <Route path="franchise/hierarchy" element={<FranchiseHierarchyPage />} />
        <Route path="franchise/agreements" element={<AgreementManagementPage />} />
        <Route path="franchise/tiers" element={<TierDefinitionsPage />} />
        <Route path="franchise/tiers/:franchiseId/upgrade" element={<TierUpgradeDowngradePage />} />
        <Route path="franchise/services" element={<ServiceCataloguePage />} />
        <Route path="franchise/provisioning" element={<ProvisioningWizardPage />} />
        <Route path="franchise/devices" element={<DeviceProvisioningPage />} />
        <Route path="franchise/certification" element={<CertificationTrackerPage />} />

        {/* Franchise — settlement / revenue / inventory / broadcasts (Phase 4b) */}
        <Route path="franchise/national-map" element={<NationalMapPage />} />
        <Route path="franchise/settlements" element={<FranchiseSettlementTrackingPage />} />
        <Route path="franchise/remittance" element={<RemittanceSchedulingPage />} />
        <Route path="franchise/revenue-share" element={<RevenueShareDisplayPage />} />
        <Route path="franchise/incentives" element={<IncentiveCalculationPage />} />
        <Route path="franchise/performance" element={<FranchisePerformancePage />} />
        <Route path="franchise/reconciliation" element={<FranchiseReconciliationPage />} />
        <Route path="franchise/suspension" element={<FranchiseSuspensionPage />} />
        <Route path="franchise/inventory" element={<InventoryManagementPage />} />
        <Route path="franchise/training" element={<TrainingDashboardPage />} />
        <Route path="franchise/transactions" element={<TransactionLogViewerPage />} />
        <Route path="franchise/discrepancies" element={<DiscrepancyDetectionPage />} />
        <Route path="franchise/broadcasts" element={<BroadcastsPage />} />

        {/* Security (Phase 4c) */}
        <Route path="security" element={<SecurityDashboardPage />} />
        <Route path="security/users" element={<UserManagementPage />} />
        <Route path="security/users/:id" element={<UserProfilePage />} />
        <Route path="security/roles" element={<RoleManagementPage />} />
        <Route path="security/field-permissions" element={<FieldPermissionsPage />} />
        <Route path="security/audit-log" element={<AuditLogPage />} />

        {/* Financial services (Phase 4c) */}
        <Route path="financial-services" element={<FinancialServicesDashboardPage />} />
        <Route path="financial-services/bill-payments" element={<BillPaymentsPage />} />
        <Route path="financial-services/transfers" element={<MoneyTransfersPage />} />
        <Route path="financial-services/pos" element={<POSTransactionsPage />} />
        <Route path="financial-services/fraud" element={<FraudAlertsPage />} />

        {/* Reporting (Phase 7a) */}
        <Route path="reporting" element={<ReportingDashboardPage />} />
        <Route path="reporting/dashboards/builder" element={<DashboardBuilderPage />} />
        <Route path="reporting/dashboards/sharing" element={<DashboardSharingPage />} />
        <Route path="reporting/reports/builder" element={<ReportBuilderPage />} />
        <Route path="reporting/reports/:id" element={<ReportViewerPage />} />
        <Route path="reporting/reports/:id/versions" element={<ReportVersioningPage />} />
        <Route path="reporting/scheduled" element={<ScheduledReportsPage />} />
        <Route path="reporting/access-audit" element={<ReportAccessAuditPage />} />
        <Route path="reporting/adhoc" element={<AdHocQueryPage />} />
        <Route path="reporting/kpi-config" element={<KPIConfigurationPage />} />
        <Route path="reporting/alerts" element={<AlertConfigurationPage />} />
        <Route path="reporting/management-kpis" element={<ManagementKPIReportsPage />} />
        <Route path="reporting/benchmarks" element={<BenchmarkComparisonsPage />} />
        <Route path="reporting/data-mining" element={<DataMiningPage />} />
        <Route path="reporting/role-scoped" element={<RoleScopedDashboardsPage />} />
        <Route path="reporting/forecasting" element={<TrendForecastingPage />} />
        <Route path="reporting/whatif" element={<WhatIfScenariosPage />} />

        {/* Reports — pre-built (Phase 7a) */}
        <Route path="reports" element={<ReportsIndexPage />} />
        <Route path="reports/daily-transactions" element={<DailyTransactionReportPage />} />
        <Route path="reports/delivery-sla" element={<DeliverySLAReportPage />} />
        <Route path="reports/fleet-ops" element={<FleetOperationsReportPage />} />
        <Route path="reports/kyc-compliance" element={<KYCComplianceReportPage />} />
        <Route path="reports/audit-compliance" element={<AuditComplianceReportPage />} />
        <Route path="reports/reconciliation" element={<ReconciliationReportPage />} />
        <Route path="reports/revenue-sharing" element={<RevenueSharingStatementPage />} />
        <Route path="reports/staff-productivity" element={<StaffProductivityReportPage />} />
        <Route path="reports/gov-services-monthly" element={<GovServicesMonthlyPage />} />
        <Route path="reports/franchise-scorecard" element={<FranchiseScorecardPage />} />

        {/* Settings (Phase 7b) */}
        <Route path="settings" element={<SettingsHubPage />} />
        <Route path="settings/system-parameters" element={<SystemParametersPage />} />
        <Route path="settings/tier-service-matrix" element={<TierServiceMatrixPage />} />
        <Route path="settings/pricing-config" element={<PricingConfigPage />} />
        <Route path="settings/pricing-zones" element={<PricingZonesPage />} />
        <Route path="settings/service-classes" element={<ServiceClassesPage />} />
        <Route path="settings/surcharges" element={<PricingSurchargesPage />} />
        <Route path="settings/discount-rules" element={<DiscountRulesPage />} />
        <Route path="settings/fraud-rules" element={<FraudRuleBuilderPage />} />
        <Route path="settings/transaction-limits" element={<TransactionLimitsPage />} />
        <Route path="settings/sla-config" element={<SLAConfigPage />} />
        <Route path="settings/gov-service-config" element={<GovServiceConfigPage />} />
        <Route path="settings/outlet-management" element={<OutletManagementPage />} />
        <Route path="settings/notification-templates" element={<NotificationTemplatesPage />} />
        <Route path="settings/recipient-accounts" element={<RecipientAccountsPage />} />
        <Route path="settings/revenue-sharing" element={<RevenueSharingConfigPage />} />

        {/* Integration (Phase 7b) */}
        <Route path="integration" element={<IntegrationDashboardPage />} />
        <Route path="integration/health" element={<IntegrationHealthPage />} />
        <Route path="integration/api-keys" element={<APIKeyManagementPage />} />
        <Route path="integration/webhooks" element={<WebhookConfigPage />} />
        <Route path="integration/partners" element={<PartnerOnboardingPage />} />
        <Route path="integration/nibss" element={<NIBSSIntegrationPage />} />

        {/* Admin → Customers */}
        <Route path="admin/customers" element={<CustomersBrowsePage />} />
        <Route path="admin/customers/dashboard" element={<CustomersDashboardPage />} />
        <Route path="admin/customers/:id" element={<CustomerDetailPage />} />

        {/* Admin (Phase 7b) */}
        <Route path="admin/email-templates" element={<EmailTemplatesPage />} />
        <Route path="admin/notifications" element={<NotificationAdminPage />} />
        <Route path="admin/mvp-acceptance" element={<MVPAcceptancePage />} />

        {/* Flowcharts + sitemap (Phase 7b) */}
        <Route path="flowcharts" element={<FlowchartsIndexPage />} />
        <Route path="flowcharts/:id" element={<FlowchartDetailPage />} />
        <Route path="sitemap" element={<SitemapPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
